import { ContentType } from '@/lib/types/content'
import { BaseStats } from '@/lib/types/interaction'
import { z } from 'zod'
import { baseStateSchema } from './base'

// Target validation schema
export const targetSchema = z.object({
  id: z.string(),
  type: z.nativeEnum(ContentType),
  productId: z.string().nullable().optional(),
  articleId: z.string().nullable().optional(),
  projectId: z.string().nullable().optional(),
  locationId: z.string().nullable().optional(),
  serviceId: z.string().nullable().optional()
})

// Stats validation schema
export const statsSchema = z.object({
  viewCount: z.number().default(0),
  favouriteCount: z.number().default(0),
  ratingCount: z.number().default(0),
  averageRating: z.number().default(0),
  commentCount: z.number().default(0),
  reviewCount: z.number().default(0),
  shares: z.object({
    facebook: z.number(),
    x: z.number(),
    reddit: z.number(),
    instagram: z.number(),
    linkedin: z.number()
  }).optional()
}).refine((data): data is BaseStats => true)

// Cart validation schemas
export const cartProductSchema = z.object({
  id: z.string(),
  name: z.string(),
  price: z.number(),
  image: z.string().optional()
})

export const cartLineItemSchema = z.object({
  id: z.string(),
  productId: z.string(),
  quantity: z.number().int().min(1),
  product: cartProductSchema,
  attributes: z.record(z.string(), z.unknown()).optional(),
  addedAt: z.string(),
  createdAt: z.string(),
  updatedAt: z.string()
})

export const cartContainerSchema = z.object({
  id: z.string(),
  items: z.array(cartLineItemSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
  status: z.enum(['active', 'completed', 'abandoned']),
  totalAmount: z.number()
})

export const cartStateSchema = z.object({
  ...baseStateSchema.shape,
  container: cartContainerSchema.nullable()
})

export const cartItemActionSchema = z.object({
  productId: z.string(),
  quantity: z.number().int().min(1),
  attributes: z.record(z.string(), z.unknown()).optional()
})

// Recently viewed validation schemas
export const recentlyViewedItemSchema = z.object({
  id: z.string(),
  path: z.string(),
  href: z.string(),
  title: z.string().optional(),
  icon: z.string().optional(),
  color: z.string().optional(),
  viewCount: z.number().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string(),
  type: z.nativeEnum(ContentType),
  targetId: z.string().optional(),
  target: targetSchema.optional(),
  slug: z.string().optional(),
  stats: statsSchema.optional().default({
    viewCount: 0,
    favouriteCount: 0,
    ratingCount: 0,
    averageRating: 0,
    commentCount: 0,
    reviewCount: 0
  }),
  details: z.object({
    address: z.string().optional(),
    city: z.string().optional()
  }).optional(),
  media: z.array(z.any()).optional(),
  category: z.any().optional()
})

export const recentlyViewedStateSchema = z.object({
  ...baseStateSchema.shape,
  items: z.array(recentlyViewedItemSchema),
  maxItems: z.number().default(5)
})

export const recentlyViewedActionSchema = recentlyViewedItemSchema

// Type inference helpers
export type CartProductSchema = z.infer<typeof cartProductSchema>
export type CartLineItemSchema = z.infer<typeof cartLineItemSchema>
export type CartContainerSchema = z.infer<typeof cartContainerSchema>
export type CartStateSchema = z.infer<typeof cartStateSchema>
export type CartItemActionSchema = z.infer<typeof cartItemActionSchema>
export type TargetSchema = z.infer<typeof targetSchema>
export type StatsSchema = z.infer<typeof statsSchema>
export type RecentlyViewedItemSchema = z.infer<typeof recentlyViewedItemSchema>
export type RecentlyViewedStateSchema = z.infer<typeof recentlyViewedStateSchema>
export type RecentlyViewedActionSchema = z.infer<typeof recentlyViewedActionSchema>
